import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ChatBox } from './ChatBox';
import { useConversations } from './useConversation';

type Props = {
  bookingId: string | null;
  onClose: () => void;
};

/**
 * Drawer wrapper around <ChatBox/> that resolves a booking id to its
 * conversation id by looking it up in the inbox list (which is cached by
 * react-query so the lookup is normally synchronous after first load).
 */
export function BookingChatDrawer({ bookingId, onClose }: Props) {
  const { t } = useTranslation();
  const { conversations, isLoading } = useConversations({ pollMs: 30000 });
  const conv = bookingId ? conversations.find((c) => c.bookingId === bookingId) : null;
  const open = bookingId != null;
  const otherName = conv?.otherParticipant.businessName ?? conv?.otherParticipant.name ?? '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flex: '0 0 auto',
        }}
      >
        <Typography sx={{ fontWeight: 700, flex: 1 }}>{otherName || t('chat.title')}</Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : conv ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ChatBox conversationId={conv.id} />
          </Box>
        ) : (
          <Box
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('chat.notAvailable')}
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
